import type {
  TimerSandbox,
  TimerSandboxOptions,
  CreateTimerSandbox,
} from './timer-sandbox.types.js'

/**
 * createTimerSandbox — factory for a tracked timer registry.
 *
 * All timer IDs (setTimeout, setInterval, requestAnimationFrame) are stored in
 * internal Set registries. On destroy(), all pending timers are cleared
 * automatically via the appropriate cancel API.
 *
 * After destroy(), all wrapped timer functions become no-ops and return 0.
 * This prevents timers from being registered on a dead sandbox.
 *
 * Design: Phase 151 TIMER-NETWORK-SANDBOX-DESIGN.md
 * Interface: timer-sandbox.types.ts
 */
export const createTimerSandbox: CreateTimerSandbox = (options?: TimerSandboxOptions): TimerSandbox => {
  const win = options?.windowImpl ?? (globalThis as unknown as NonNullable<TimerSandboxOptions['windowImpl']>)

  const registry = {
    timeout: new Set<number>(),
    interval: new Set<number>(),
    rAF: new Set<number>(),
  }

  let destroyed = false

  return {
    setTimeout(handler, timeout?, ...args) {
      if (destroyed) return 0
      const id = win.setTimeout(handler as TimerHandler, timeout, ...args)
      registry.timeout.add(id)
      return id
    },

    clearTimeout(id) {
      if (id === undefined) return
      win.clearTimeout(id)
      registry.timeout.delete(id)
    },

    setInterval(handler, timeout?, ...args) {
      if (destroyed) return 0
      const id = win.setInterval(handler as TimerHandler, timeout, ...args)
      registry.interval.add(id)
      return id
    },

    clearInterval(id) {
      if (id === undefined) return
      win.clearInterval(id)
      registry.interval.delete(id)
    },

    requestAnimationFrame(callback) {
      if (destroyed) return 0
      const id = win.requestAnimationFrame(callback)
      registry.rAF.add(id)
      return id
    },

    cancelAnimationFrame(id) {
      win.cancelAnimationFrame(id)
      registry.rAF.delete(id)
    },

    pendingCount() {
      return {
        timeout: registry.timeout.size,
        interval: registry.interval.size,
        rAF: registry.rAF.size,
      }
    },

    destroy() {
      registry.timeout.forEach((id) => win.clearTimeout(id))
      registry.interval.forEach((id) => win.clearInterval(id))
      registry.rAF.forEach((id) => win.cancelAnimationFrame(id))
      registry.timeout.clear()
      registry.interval.clear()
      registry.rAF.clear()
      destroyed = true
    },
  }
}

// Local alias to avoid TypeScript complaint about unknown[] vs the native parameter type
type TimerHandler = (...args: unknown[]) => void
